/* eslint-disable max-classes-per-file */
/* eslint-disable @typescript-eslint/naming-convention */
import { ColumnState } from 'ag-grid-community';

import { AnalyticsEvent } from '@shure/shared/models';

import { CdmAnalyticsEvents } from './cdm-analytics-events.enum';
import { AnalyticsLocation } from './common.model';

export interface TableDefinition {
	table_name: DeviceManagementTableName;
	view_type?: DeviceManagementTableViewTypeMap[DeviceManagementTableName];
}

export type DeviceManagementTableName =
	| AnalyticsLocation.DevicesTable
	| AnalyticsLocation.PendingTable
	| AnalyticsLocation.JobsTable
	| AnalyticsLocation.CreateJobTable
	| AnalyticsLocation.JobDetailsTable
	| AnalyticsLocation.FirmwareUpdateDialogTable;

export type DeviceManagementTableViewTypeMap = {
	[AnalyticsLocation.DevicesTable]: 'flat' | 'connections' | 'spaces';
	[AnalyticsLocation.PendingTable]: undefined;
	[AnalyticsLocation.JobsTable]: undefined;
	[AnalyticsLocation.CreateJobTable]: undefined;
	[AnalyticsLocation.JobDetailsTable]: 'view' | 'edit';
	[AnalyticsLocation.FirmwareUpdateDialogTable]: undefined;
};

export enum ExportType {
	Csv = 'csv',
	Excel = 'excel'
}

/**
 * To measure which column settings users find most useful.
 *
 * When the user navigates to the devices page, the user's table settings that were saved in the user's preferences will be tracked.
 * This info includes which columns are visible, if the default view is the "flat" view or the "connections" view, column sorting, etc.
 * This will help understand which table settings users are most interested in seeing.
 *
 * At the moment, user's table settings are only preserved for the devices inventory table.
 */
export class TableColumnSettingsChangedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.TableColumnSettingsChanged;
	public readonly properties: {
		table_definition: TableDefinition;
		column_state: ColumnState[];
	};

	constructor(tableDefinition: TableDefinition, columnState: ColumnState[]) {
		this.properties = { table_definition: tableDefinition, column_state: columnState };
	}
}

/**
  * To measure when users export data from tables, and in which format.
  * This will be tracked each time the user clicks/selects an export option associated with a data table
.*/
export class TableExportedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.TableExported;
	public readonly properties: {
		table_definition: TableDefinition;
		type: ExportType;
	};

	constructor(tableDefinition: TableDefinition, type: ExportType) {
		this.properties = { table_definition: tableDefinition, type };
	}
}

/**
  * To measure when the view of a table is switched.
  * This will be tracked each time the user swaps between inventory and connections view in the devices table.
.*/
export class TableViewChangedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.TableViewChanged;
	public readonly properties: {
		table_definition: TableDefinition;
	};

	constructor(
		tableName: DeviceManagementTableName,
		viewType: DeviceManagementTableViewTypeMap[DeviceManagementTableName]
	) {
		this.properties = { table_definition: { table_name: tableName, view_type: viewType } };
	}
}
